/**
 * Compute → Requests (D447): people asking the organisation to approve a cluster. Those waiting come first with Approve and
 * Decline; the decided ones sit below, quieter. Approving adds the cluster as it was asked for.
 */

import type { ComputeClusterRequest } from '@aioe/shared';
import { CircleCheck, CircleQuestion, Server } from '@gravity-ui/icons';
import { useState } from 'react';

import { computeRoutes, problemText } from '../../api/client.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { usePersonName } from '../foundry/foundryWords.ts';
import { Button, ErrorNote, inputClass, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark, Empty } from '../ui.tsx';
import { FormField, FormSheet } from '../ui/FormSheet.tsx';
import { KIND_WORDS, plural, poolWords, requestTarget, TONE, whereWords } from './computeWords.ts';

function Who({ q }: { q: ComputeClusterRequest }) {
  const name = usePersonName(q.askedByName ? undefined : q.askedBy);
  return <>{q.askedByName ?? name ?? 'Someone'}</>;
}

function RequestDescription({ q }: { q: ComputeClusterRequest }) {
  const pools = poolWords(q.cluster);
  return (
    <>
      <span className="block">
        <Who q={q} /> · {KIND_WORDS[q.kind]} · {whereWords(q.cluster)}
      </span>
      {pools.length ? <span className="block truncate">{plural(pools.length, 'node pool')}: {pools.join('; ')}</span> : null}
      {q.reason ? <span className="block">“{q.reason}”</span> : null}
      <span className="block tabular-nums">Asked {timeAgo(q.createdAt)}</span>
    </>
  );
}

export function RequestsPanel({ mayEdit }: { mayEdit: boolean }) {
  const list = useLoad(() => computeRoutes.requests(), [], 30_000);
  const [answering, setAnswering] = useState<{ q: ComputeClusterRequest; decision: 'approve' | 'decline' }>();
  const all = list.data?.requests;
  const pending = (all ?? []).filter((q) => q.state === 'pending').sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const decided = (all ?? []).filter((q) => q.state !== 'pending').sort((a, b) => (b.decidedAt ?? b.createdAt).localeCompare(a.decidedAt ?? a.createdAt));
  const approved = decided.filter((q) => q.state === 'approved').length;

  return (
    <PageColumn width="data">
      <PageHeader tone={TONE} icon={<CircleQuestion />} title="Cluster requests" subtitle="The clusters people have asked to use that the organisation has not approved yet." />
      <ErrorNote error={list.error} />
      <div className="art-stats">
        <Stat tone={TONE} icon={<CircleQuestion />} label="Waiting for you" value={all ? pending.length : undefined} detail={pending.length ? `Oldest ${timeAgo(pending[0]!.createdAt)}` : 'Nothing waiting'} needsYou={pending.length > 0} />
        <Stat tone={TONE} icon={<CircleCheck />} label="Approved" value={all ? approved : undefined} detail="Added as clusters" />
        <Stat tone={TONE} icon={<Server />} label="Declined" value={all ? decided.length - approved : undefined} detail="The person was told" />
      </div>

      <ListGroup
        title="Waiting for a decision"
        count={all ? pending.length : undefined}
        description="Approving adds the cluster as it was asked for, so everyone you serve can use it. Declining tells the person, with your note."
        empty={all && pending.length === 0 ? <Empty tone={TONE} icon={<CircleQuestion />} title="Nothing waiting" body="When someone asks to use a cluster the organisation has not approved, the request lands here." /> : undefined}
      >
        {!all && !list.error ? <ListRow title="Reading the requests…" /> : null}
        {pending.map((q) => (
          <ListRow
            key={q.requestId}
            icon={<Server />}
            title={requestTarget(q)}
            description={<RequestDescription q={q} />}
            control={
              mayEdit ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="primary" onPress={() => setAnswering({ q, decision: 'approve' })}>
                    Approve
                  </Button>
                  <Button size="sm" variant="secondary" onPress={() => setAnswering({ q, decision: 'decline' })}>
                    Decline
                  </Button>
                </div>
              ) : undefined
            }
          />
        ))}
      </ListGroup>

      {decided.length ? (
        <ListGroup title="Decided" count={decided.length}>
          {decided.map((q) => (
            <ListRow
              key={q.requestId}
              icon={<Server />}
              title={requestTarget(q)}
              description={
                <>
                  <RequestDescription q={q} />
                  {q.note ? <span className="block">Your note: {q.note}</span> : null}
                </>
              }
              status={<StatusMark state={q.state === 'approved' ? 'connected' : 'disabled'} label={q.state === 'approved' ? 'Approved' : 'Declined'} />}
            />
          ))}
        </ListGroup>
      ) : null}

      {answering ? (
        <DecisionSheet
          {...answering}
          onClose={() => setAnswering(undefined)}
          onDone={() => {
            setAnswering(undefined);
            list.reload();
          }}
        />
      ) : null}
    </PageColumn>
  );
}

/** The small sheet for one decision: an optional note, then the answer. */
function DecisionSheet({ q, decision, onClose, onDone }: { q: ComputeClusterRequest; decision: 'approve' | 'decline'; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const approving = decision === 'approve';
  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await computeRoutes.decideRequest(q.requestId, { decision, ...(note.trim() ? { note: note.trim() } : {}) });
      onDone();
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };
  return (
    <FormSheet
      width="md"
      title={`${approving ? 'Approve' : 'Decline'} ${requestTarget(q)}`}
      description={approving ? 'The cluster is added as it was asked for, with its node pools, and the organisation’s policy is updated. You can change it afterwards under Clusters.' : 'The person is told it was declined, with your note if you add one.'}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button variant={approving ? 'primary' : 'danger-soft'} onPress={() => void send()} isPending={busy}>
            {approving ? 'Approve' : 'Decline'}
          </Button>
        </>
      }
    >
      <FormField label={approving ? 'A note for the person (optional)' : 'Why (optional)'}>
        <textarea className={`${inputClass} resize-y`} rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}
